import cytoscape, { Core } from 'cytoscape';

import { rwColors } from '@/styles/colors';
import { NodeType } from '../types/cytoscape';

// 定数の定義（可読性向上のため、ファイル冒頭に集約）
const DEFAULT_NODE_COLOR = '#95a5a6';
const DEFAULT_BORDER_WIDTH = '2px';
const MIN_NODE_WIDTH = 20;
const MIN_NODE_HEIGHT = 16;
const MIN_CELL_SIZE = 64;
const CELL_SIZE_MULTIPLIER = 1.5;
const ELLIPSE_RADIUS_X_FACTOR = 1.6;
const ELLIPSE_RADIUS_Y_FACTOR = 1.0;
const SPIRAL_SPREAD_FACTOR_SINGLE = 70;
const SPIRAL_SPREAD_FACTOR_MULTI = 10;
const MULTI_EDGE_OVERLAP_PADDING = 12;
const MULTI_EDGE_OVERLAP_ITERATIONS = 120;
const DAMPING_FACTOR = 0.9;
const POSITION_CLAMP_FACTOR = 3; // 座標の許容範囲倍率

/**
 * ノードタイプに基づいて色を返す（リファクタリング: デフォルト値を定数化）
 */
export const getNodeColor = (node_type: NodeType): string => {
  // nodeTypeに基づいて色を返す
  switch (node_type) {
    case NodeType.THEME:
      return NODE_STYLE_CONFIG[NodeType.THEME]['background-color'];
    case NodeType.CROSS:
      return NODE_STYLE_CONFIG[NodeType.CROSS]['background-color'];
    case NodeType.CROSS_ISSUE:
      return NODE_STYLE_CONFIG[NodeType.CROSS_ISSUE]['background-color'];
    case NodeType.SOCIAL_ISSUE:
      return NODE_STYLE_CONFIG[NodeType.SOCIAL_ISSUE]['background-color'];
    case NodeType.ASSIST:
      return NODE_STYLE_CONFIG[NodeType.ASSIST]['background-color'];
    case NodeType.NORMAL:
      return NODE_STYLE_CONFIG[NodeType.NORMAL]['background-color'];
    default:
      return DEFAULT_NODE_COLOR;
  }
};
export const getNodeBorderColor = (node_type: NodeType): string => {
  // nodeTypeに基づいて色を返す
  switch (node_type) {
    case NodeType.THEME:
      return NODE_STYLE_CONFIG[NodeType.THEME]['border-color'];
    case NodeType.CROSS:
      return NODE_STYLE_CONFIG[NodeType.CROSS]['border-color'];
    case NodeType.CROSS_ISSUE:
      return NODE_STYLE_CONFIG[NodeType.CROSS_ISSUE]['border-color'];
    case NodeType.SOCIAL_ISSUE:
      return NODE_STYLE_CONFIG[NodeType.SOCIAL_ISSUE]['border-color'];
    case NodeType.ASSIST:
      return NODE_STYLE_CONFIG[NodeType.ASSIST]['border-color'];
    case NodeType.NORMAL:
      return NODE_STYLE_CONFIG[NodeType.NORMAL]['border-color'];
    default:
      return rwColors['light-gray']; // デフォルトはグレー
  }
};
export const getNodeBorderWidth = (node_type: NodeType): string => {
  // nodeTypeに基づいて色を返す
  switch (node_type) {
    case NodeType.THEME:
      return NODE_STYLE_CONFIG[NodeType.THEME]['border-width'] || '1px';
    case NodeType.CROSS:
      return NODE_STYLE_CONFIG[NodeType.CROSS]['border-width'] || '1px';
    case NodeType.CROSS_ISSUE:
      return NODE_STYLE_CONFIG[NodeType.CROSS_ISSUE]['border-width'] || '1px';
    case NodeType.SOCIAL_ISSUE:
      return NODE_STYLE_CONFIG[NodeType.SOCIAL_ISSUE]['border-width'] || '1px';
    case NodeType.ASSIST:
      return NODE_STYLE_CONFIG[NodeType.ASSIST]['border-width'] || '1px';
    case NodeType.NORMAL:
      return NODE_STYLE_CONFIG[NodeType.NORMAL]['border-width'] || '1px';
    default:
      return DEFAULT_BORDER_WIDTH; // デフォルトは 2px
  }
};
export const getNodeBackgroundOpacity = (node_type: NodeType): number => {
  return NODE_STYLE_CONFIG[node_type]['background-opacity'] || 0;
};
export const getNodeTextColor = (node_type: NodeType): string => {
  return NODE_STYLE_CONFIG[node_type]['text-color'] || rwColors.black;
};

// コンテナサイズとズーム状態を管理
const graphState = {
  width: 800, // デフォルト値
  height: 600, // デフォルト値
  zoom: 1, // デフォルトズーム
};

/**
 * グラフの状態を更新する関数
 * @param cy Cytoscapeインスタンス
 */
export const updateGraphState = (cy: cytoscape.Core) => {
  if (!cy) return;
  graphState.width = cy.width();
  graphState.height = cy.height();
  graphState.zoom = cy.zoom();
};

/**
 * ノードの重みとグラフサイズに応じた動的なサイズを返す
 * @param weight ノードの重み
 * @param nodeType ノードタイプ（オプション）
 * @param minSizeFactor 最小サイズの係数
 * @param maxSizeFactor 最大サイズの係数
 * @returns 計算されたノードサイズ
 */
export const getNodeSize = (
  weight: number,
  nodeType?: NodeType,
  minSizeFactor: number = 0.01,
  maxSizeFactor: number = 0.05
): number => {
  // 重みが0以下の場合は最小サイズを返す
  if (weight <= 0) return getMinNodeSize(minSizeFactor);

  // 表示領域の対角線の長さを計算
  const diagonalLength = Math.sqrt(
    graphState.width * graphState.width + graphState.height * graphState.height
  );

  // 対角線に基づいて最小・最大サイズを計算
  const minSize = diagonalLength * minSizeFactor;
  const maxSize = diagonalLength * maxSizeFactor;

  // 重みを対数スケールで変換（大きな差を緩和）
  const logWeight = Math.log(weight + 1);

  // 重みの最大値は経験的に設定（必要に応じて調整）
  const maxLogWeight = Math.log(100000 + 1);

  // 基本サイズの計算
  let baseSize =
    minSize + (maxSize - minSize) * Math.min(logWeight / maxLogWeight, 1) * 1.5;

  // ノードタイプに応じたサイズ調整
  switch (nodeType) {
    case NodeType.THEME:
      baseSize = baseSize * 2.0; // テーマノードは少し大きく
      break;
    case NodeType.CROSS:
      baseSize = baseSize * 1.0; // クロスノードはさらに大きく
      break;
    case NodeType.SOCIAL_ISSUE:
      baseSize = baseSize * 1.0; // 社会課題ノードは少し大きく
      break;
    case NodeType.CROSS_ISSUE:
      baseSize = baseSize * 1.0; // 共有社会課題ノードは大きめ
      break;
    case NodeType.ASSIST:
      baseSize = baseSize * 1.0; // ASSISTノードは小さめ
      break;
    case NodeType.NORMAL:
      baseSize = baseSize * 1.0; // NORMALノードは少し小さめ
      break;
    default:
      baseSize = baseSize * 1.0; // その他は基本サイズを返す
      break;
  }

  // ズームレベルに応じた調整（ズームアウト時は大きく、ズームイン時は小さく）
  const zoomAdjustment =
    graphState.zoom < 1
      ? Math.pow(1 / graphState.zoom, 0.2) // ズームアウト時の調整
      : Math.pow(1 / graphState.zoom, 0.1); // ズームイン時の調整

  return baseSize * zoomAdjustment;
};

// 最小ノードサイズを取得する補助関数
export const getMinNodeSize = (minSizeFactor: number = 0.01): number => {
  const diagonalLength = Math.sqrt(
    graphState.width * graphState.width + graphState.height * graphState.height
  );
  return diagonalLength * minSizeFactor;
};

export const NODE_STYLE_CONFIG = {
  [NodeType.THEME]: {
    'background-color': rwColors.sub, //  (テーマ)
    'border-color': rwColors.black,
    'border-width': '2px', //  (テーマノードの境界線)
    'background-opacity': 0.9, //  (テーマノードの背景不透明度)
    'text-color': rwColors['white'], //  (テーマノードのテキスト色)
  },
  [NodeType.CROSS]: {
    'background-color': rwColors['white'], //  (クロスノード)
    'border-color': rwColors.black,
    'border-width': '1px', //  (クロスノードの境界線)
    'background-opacity': 0.8, //  (クロスノードの背景不透明度)
    'text-color': rwColors['black-gray'], //  (クロスノードのテキスト色)
  },
  [NodeType.SOCIAL_ISSUE]: {
    'background-color': rwColors['white'], //  (社会課題)
    'border-color': rwColors.black,
    'border-width': '1px', //  (社会課題ノードの境界線)
    'background-opacity': 0.8, //  (社会課題ノードの背景不透明度)
    'text-color': rwColors.red, //  (社会課題ノードのテキスト色)
  },
  [NodeType.CROSS_ISSUE]: {
    'background-color': rwColors['white'], //  (共有社会課題)
    'border-color': rwColors.black,
    'border-width': '1px', //  (共有社会課題ノードの境界線)
    'background-opacity': 0.8, //  (共有社会課題ノードの背景不透明度)
    'text-color': rwColors.red, //  (共有社会課題ノードのテキスト色)
  },
  [NodeType.ASSIST]: {
    'background-color': rwColors['white'], //  (ASSISTノード用)
    'border-color': rwColors.black,
    'border-width': '1px', //  (ASSISTノードの境界線)
    'background-opacity': 0.8, //  (ASSISTノードの背景不透明度)
    'text-color': '#000000', //  (ASSISTノードのテキスト色)
  },
  [NodeType.NORMAL]: {
    'background-color': rwColors['white'], //  (NORMALノード用)
    'border-color': rwColors.black,
    'border-width': '1px', //  (NORMALノードの境界線)
    'background-opacity': 0.8, //  (NORMALノードの背景不透明度)
    'text-color': '#000000', //  (NORMALノードのテキスト色)
  },
  [NodeType.REGION_ECONOMIC_UNION]: {
    'background-color': rwColors['white'], //  (地域経済連合タグ)
    'border-color': rwColors.black,
    'border-width': '1px', //  (地域経済連合タグの境界線)
    'background-opacity': 0.8, //  (地域経済連合タグの背景不透明度)
    'text-color': '#000000', //  (地域経済連合タグのテキスト色)
  },
  [NodeType.COUNTRY]: {
    'background-color': rwColors['white'], //  (国タグ)
    'border-color': rwColors.black,
    'border-width': '1px', //  (国タグの境界線)
    'background-opacity': 0.8, //  (国タグの背景不透明度)
    'text-color': '#000000', //  (国タグのテキスト色)
  },
  [NodeType.ORGANIZATION_TYPE]: {
    'background-color': rwColors['white'], //  (組織形態タグ)
    'border-color': rwColors.black,
    'border-width': '1px', //  (組織形態タグの境界線)
    'background-opacity': 0.8, //  (組織形態タグの背景不透明度)
    'text-color': '#000000', //  (組織形態タグのテキスト色)
  },
} as const;

function resolveOverlapsOptimized(
  width: number,
  height: number,
  nodes: cytoscape.NodeCollection,
  options?: {
    padding?: number;
    iterations?: number;
    preferVertical?: boolean;
  }
) {
  const padding = options?.padding ?? 6;
  const iterations = options?.iterations ?? 6;
  const preferVertical = options?.preferVertical ?? true;

  const nodeArray = nodes.toArray(); // 変数名を明確に
  const nodeCount = nodeArray.length;
  if (nodeCount <= 1) return;

  // サブ関数: ノードサイズの計算
  const calculateNodeSizes = (nodes: cytoscape.NodeSingular[]) => {
    return nodes.map((node) => {
      try {
        if (!node || !node.cy) throw new Error('invalid node');
        const boundingBox = node.boundingBox({ includeLabels: true }) || {};
        const width = Math.max(
          MIN_NODE_WIDTH,
          (boundingBox.w as number) || MIN_NODE_WIDTH
        );
        const height = Math.max(
          MIN_NODE_HEIGHT,
          (boundingBox.h as number) || MIN_NODE_HEIGHT
        );
        return { width, height };
      } catch {
        // boundingBox の取得や node の参照が失敗した場合はフォールバックサイズを返す
        return { width: MIN_NODE_WIDTH, height: MIN_NODE_HEIGHT };
      }
    });
  };

  const sizes = calculateNodeSizes(nodeArray);
  const avgSize =
    sizes.reduce((sum, size) => sum + (size.width + size.height) / 2, 0) /
    Math.max(1, nodeCount);
  const cellSize = Math.max(
    MIN_CELL_SIZE,
    avgSize * CELL_SIZE_MULTIPLIER + padding
  );

  // サブ関数: 重なりチェックと移動計算
  const checkAndResolveOverlap = (
    nodeA: cytoscape.NodeSingular,
    nodeB: cytoscape.NodeSingular,
    sizeA: { width: number; height: number },
    sizeB: { width: number; height: number },
    padding: number,
    preferVertical: boolean
  ): {
    moveAX: number;
    moveAY: number;
    moveBX: number;
    moveBY: number;
  } | null => {
    const posA = nodeA.position();
    const posB = nodeB.position();

    const leftA = posA.x - sizeA.width / 2;
    const rightA = posA.x + sizeA.width / 2;
    const topA = posA.y - sizeA.height / 2;
    const bottomA = posA.y + sizeA.height / 2;

    const leftB = posB.x - sizeB.width / 2;
    const rightB = posB.x + sizeB.width / 2;
    const topB = posB.y - sizeB.height / 2;
    const bottomB = posB.y + sizeB.height / 2;

    const overlapX = Math.max(
      0,
      Math.min(rightA, rightB) - Math.max(leftA, leftB)
    );
    const overlapY = Math.max(
      0,
      Math.min(bottomA, bottomB) - Math.max(topA, topB)
    );

    if (overlapX <= 0 || overlapY <= 0) return null;

    // 移動ベクトルの計算（垂直優先ロジックを簡略化）
    let moveAX = 0,
      moveAY = 0,
      moveBX = 0,
      moveBY = 0;
    if (preferVertical && overlapY >= overlapX) {
      const push = overlapY / 2 + padding;
      const sign = posA.y <= posB.y ? -1 : 1;
      moveAY = sign * push;
      moveBY = -sign * push;
      if (overlapX > 0) {
        const pushX = (overlapX / 2 + padding) * 0.3;
        const signX = posA.x <= posB.x ? -1 : 1;
        moveAX = signX * pushX;
        moveBX = -signX * pushX;
      }
    } else {
      let dx = posA.x - posB.x;
      let dy = posA.y - posB.y;
      let dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 1e-2) {
        const angle = Math.random() * Math.PI * 2;
        dx = Math.cos(angle);
        dy = Math.sin(angle);
        dist = 1;
      }
      const ux = dx / dist;
      const uy = dy / dist;
      const moveX = overlapX / 2 + padding;
      const moveY = (overlapY / 2 + padding) * 1.4;
      moveAX = ux * moveX;
      moveAY = uy * moveY;
      moveBX = -ux * moveX;
      moveBY = -uy * moveY;
    }
    return { moveAX, moveAY, moveBX, moveBY };
  };

  // サブ関数: 位置の適用とクランプ
  const applyMovement = (
    nodeA: cytoscape.NodeSingular,
    nodeB: cytoscape.NodeSingular,
    moves: { moveAX: number; moveAY: number; moveBX: number; moveBY: number },
    width: number,
    height: number
  ) => {
    const posA = nodeA.position();
    const posB = nodeB.position();
    const minAllowedX = -width * POSITION_CLAMP_FACTOR;
    const maxAllowedX = width * POSITION_CLAMP_FACTOR;
    const minAllowedY = -height * POSITION_CLAMP_FACTOR;
    const maxAllowedY = height * POSITION_CLAMP_FACTOR;

    const newAx = Math.max(
      minAllowedX,
      Math.min(maxAllowedX, posA.x + moves.moveAX * DAMPING_FACTOR)
    );
    const newAy = Math.max(
      minAllowedY,
      Math.min(maxAllowedY, posA.y + moves.moveAY * DAMPING_FACTOR)
    );
    const newBx = Math.max(
      minAllowedX,
      Math.min(maxAllowedX, posB.x + moves.moveBX * DAMPING_FACTOR)
    );
    const newBy = Math.max(
      minAllowedY,
      Math.min(maxAllowedY, posB.y + moves.moveBY * DAMPING_FACTOR)
    );

    nodeA.position({ x: newAx, y: newAy });
    nodeB.position({ x: newBx, y: newBy });
  };

  for (let iter = 0; iter < iterations; iter++) {
    let anyMoved = false;
    const grid = new Map<string, number[]>();
    nodeArray.forEach((node, idx) => {
      const pos = node.position();
      const ix = Math.floor(pos.x / cellSize);
      const iy = Math.floor(pos.y / cellSize);
      const key = `${ix},${iy}`;
      const list = grid.get(key) ?? [];
      list.push(idx);
      grid.set(key, list);
    });

    for (let i = 0; i < nodeCount; i++) {
      const nodeA = nodeArray[i];
      const posA = nodeA.position();
      const ix = Math.floor(posA.x / cellSize);
      const iy = Math.floor(posA.y / cellSize);

      for (let dxCell = -1; dxCell <= 1; dxCell++) {
        for (let dyCell = -1; dyCell <= 1; dyCell++) {
          const neighborKey = `${ix + dxCell},${iy + dyCell}`;
          const bucket = grid.get(neighborKey);
          if (!bucket) continue;
          for (const j of bucket) {
            if (j <= i) continue;
            const nodeB = nodeArray[j];
            const moves = checkAndResolveOverlap(
              nodeA,
              nodeB,
              sizes[i],
              sizes[j],
              padding,
              preferVertical
            );
            if (moves) {
              anyMoved = true;
              applyMovement(nodeA, nodeB, moves, width, height);
            }
          }
        }
      }
    }
    if (!anyMoved) break;
  }
}

/**
 * マルチテーマスパイラルレイアウトを設定（リファクタリング: サブ関数に分割）
 */
export const setupMultiThemeSpiralLayout = (
  cytoscapeCoreRefObject: Core | null
) => {
  const cy = cytoscapeCoreRefObject;
  // guard: skip if cy is not available or already destroyed
  try {
    if (!cy) return;
    // Some cytoscape builds expose destroyed() method; avoid `any` by using unknown
    const maybeDestroyed = (cy as unknown as { destroyed?: () => boolean })
      .destroyed;
    const isDestroyed =
      typeof maybeDestroyed === 'function' ? maybeDestroyed.call(cy) : false;
    if (isDestroyed) return;
    // If there's no container (headless mode or not yet mounted), skip
    try {
      if (!cy.container()) return;
    } catch {
      // ignore container check failures
    }
  } catch {
    return;
  }

  const width = cy.width();
  const height = cy.height();
  const center = { x: width / 2, y: height / 2 };
  const radiusX = Math.min(width, height) * ELLIPSE_RADIUS_X_FACTOR;
  const radiusY = Math.min(width, height) * ELLIPSE_RADIUS_Y_FACTOR;

  const themeNodes = cy.nodes('[nodeType = "theme"]');
  if (themeNodes.length === 0) return;

  // ノーマルタグノードを非表示にする
  cytoscapeCoreRefObject
    .nodes(`[nodeType = "${NodeType.NORMAL}"]`)
    .style('display', 'none');

  // サブ関数: テーマノードのソート
  const sortThemeNodes = (nodes: cytoscape.NodeCollection) => {
    return nodes.sort((a, b) => {
      const labelA = a.data('themeLabels')?.[0] || '';
      const labelB = b.data('themeLabels')?.[0] || '';
      return labelA.localeCompare(labelB);
    });
  };

  const sortedThemeNodes = sortThemeNodes(themeNodes);
  let allTopicTagNodes = cy.collection();

  // サブ関数: シングルエッジノードの配置
  const positionSingleEdgeNodes = (
    connectedNodes: cytoscape.NodeCollection,
    themeNodes: cytoscape.NodeCollection,
    x: number,
    y: number
  ) => {
    const singleEdgeNodes = connectedNodes
      .filter((node) => {
        const edges = node
          .connectedEdges()
          .filter((e) =>
            themeNodes
              .toArray()
              .some(
                (tn) =>
                  tn.id() === e.source().id() || tn.id() === e.target().id()
              )
          );
        return edges.length === 1;
      })
      .sort((a, b) => (b.data('weight') || 0) - (a.data('weight') || 0));

    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    singleEdgeNodes.forEach((node, i) => {
      const radius = SPIRAL_SPREAD_FACTOR_SINGLE * Math.sqrt(i + 1);
      const angle = i * goldenAngle;
      const nx = x + radius * Math.cos(angle) + 5;
      const ny = y + radius * Math.sin(angle) + 5;
      node.position({ x: nx, y: ny });
    });
  };

  // サブ関数: マルチエッジノードの配置
  const positionMultiEdgeNodes = (
    connectedNodes: cytoscape.NodeCollection,
    themeNodes: cytoscape.NodeCollection,
    x: number,
    y: number
  ) => {
    const multiEdgeNodes = connectedNodes.filter((node) => {
      const edges = node
        .connectedEdges()
        .filter((e) =>
          themeNodes
            .toArray()
            .some(
              (tn) => tn.id() === e.source().id() || tn.id() === e.target().id()
            )
        );
      return edges.length > 1;
    });

    multiEdgeNodes.forEach((node, i) => {
      let nx = x,
        ny = y;
      const edgesToThemes = node
        .connectedEdges()
        .filter((e) =>
          themeNodes
            .toArray()
            .some(
              (tn) => tn.id() === e.source().id() || tn.id() === e.target().id()
            )
        );
      let totalWeight = 0,
        weightedX = 0,
        weightedY = 0;
      edgesToThemes.forEach((edge) => {
        const targetTheme = themeNodes
          .toArray()
          .find(
            (tn) =>
              tn.id() === edge.source().id() || tn.id() === edge.target().id()
          );
        if (!targetTheme) return;
        const pos = targetTheme.position();
        const weight = edge.data('weight') || 0;
        totalWeight += weight;
        weightedX += pos.x * weight;
        weightedY += pos.y * weight;
      });
      if (totalWeight > 0) {
        nx = weightedX / totalWeight;
        ny = weightedY / totalWeight;
      }
      const goldenAngle = Math.PI * (3 - Math.sqrt(5));
      const radius = SPIRAL_SPREAD_FACTOR_MULTI * Math.sqrt(i + 1);
      const angle = i * goldenAngle;
      nx += radius * Math.cos(angle);
      ny += radius * Math.sin(angle);
      node.position({ x: nx, y: ny });
    });
    return multiEdgeNodes;
  };

  sortedThemeNodes.forEach((themeNode, idx) => {
    const angle = (2 * Math.PI * idx) / sortedThemeNodes.length;
    const x = center.x + radiusX * Math.cos(angle);
    const y = center.y + radiusY * Math.sin(angle);
    const connectedNodes = themeNode.neighborhood('node').not(themeNodes);

    positionSingleEdgeNodes(connectedNodes, themeNodes, x, y);
    const multiEdgeNodes = positionMultiEdgeNodes(
      connectedNodes,
      themeNodes,
      x,
      y
    );
    themeNode.position({ x, y });
    if (multiEdgeNodes.length > 0) {
      allTopicTagNodes = allTopicTagNodes.union(multiEdgeNodes);
    }
  });

  // 残りのノードの配置
  const themeAndConnected = themeNodes.union(themeNodes.neighborhood('node'));
  const remainingNodes = cy.nodes().not(themeAndConnected);
  if (remainingNodes.length > 0) {
    try {
      const layout = remainingNodes.layout({
        name: 'cose',
        fit: false,
        boundingBox: {
          x1: center.x - radiusX * 2,
          y1: center.y - radiusY * 2,
          x2: center.x + radiusX * 2,
          y2: center.y + radiusY * 2,
        },
      });
      // run layout in try/catch; some layout implementations may call
      // positions() which can fail if cy is in inconsistent state
      try {
        layout.run();
      } catch (err) {
        console.error('Error running remainingNodes layout:', err);
      }
    } catch (err) {
      console.error('Failed to create/run layout for remaining nodes:', err);
    }
    allTopicTagNodes = allTopicTagNodes.union(remainingNodes);
  }

  // 重なり解消
  if (allTopicTagNodes.length > 1) {
    try {
      // 位置変更は batch 内で行い、内部の notify/endBatch による race を防ぐ
      // guard batch: only if batch exists and cy not destroyed
      const maybeDestroyed2 = (cy as unknown as { destroyed?: () => boolean })
        .destroyed;
      const canBatch =
        typeof cy.batch === 'function' &&
        !(typeof maybeDestroyed2 === 'function' && maybeDestroyed2.call(cy));
      if (canBatch) {
        cy.batch(() => {
          resolveOverlapsOptimized(width, height, allTopicTagNodes, {
            padding: MULTI_EDGE_OVERLAP_PADDING,
            iterations: MULTI_EDGE_OVERLAP_ITERATIONS,
            preferVertical: false,
          });
        });
      } else {
        // fallback: run without batch but guarded
        resolveOverlapsOptimized(width, height, allTopicTagNodes, {
          padding: MULTI_EDGE_OVERLAP_PADDING,
          iterations: MULTI_EDGE_OVERLAP_ITERATIONS,
          preferVertical: false,
        });
      }
    } catch (err) {
      // バッチ実行や重なり解消でエラーが起きても UI を壊さない
      console.error('Error during resolveOverlapsOptimized:', err);
    }
  }
};

function getLabelWidth(
  label: string,
  fontSize: number = 12,
  fontFamily: string = 'sans-serif',
  padding: number = 16
): number {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 80; // fallback
  context.font = `${fontSize}px ${fontFamily}`;
  const metrics = context.measureText(label);
  return metrics.width + padding; // 余白を追加
}

/**
 * Cytoscapeスタイルを適用（リファクタリング: スタイルをグループ化）
 */
export const applyCytoscapeStyles = (cy: cytoscape.Core) => {
  cy.style([
    // ベースノードスタイル
    {
      selector: 'node',
      style: {
        label: 'data(label)',
        shape: 'round-rectangle', // ノードの形状を丸角四角形に設定
        width: (ele: cytoscape.NodeSingular) => {
          const label = ele.data('label') || '';
          return getLabelWidth(label, 12, 'sans-serif', 16);
        },
        height: '24px',
        color: (ele: cytoscape.NodeSingular) => {
          const nodeType = ele.data('nodeType');
          return getNodeTextColor(nodeType);
        },
        'text-valign': 'center',
        'text-halign': 'center',
        'font-size': 12,
        'min-zoomed-font-size': 6,
        'z-index': 10,
        'transition-property': 'background-color, width, height',
        'transition-duration': 0.3,
        'background-opacity': (ele: cytoscape.NodeSingular) => {
          const nodeType = ele.data('nodeType');
          return getNodeBackgroundOpacity(nodeType);
        },
        'background-color': (ele: cytoscape.NodeSingular) => {
          const nodeType = ele.data('nodeType');
          return getNodeColor(nodeType);
        },
        'border-color': (ele: cytoscape.NodeSingular) => {
          const nodeType = ele.data('nodeType');
          return getNodeBorderColor(nodeType);
        },
        'border-width': (ele: cytoscape.NodeSingular) => {
          const nodeType = ele.data('nodeType');
          return getNodeBorderWidth(nodeType);
        },
      },
    },
    // エッジスタイル
    {
      selector: 'edge',
      style: {
        width: 0.1,
        'line-color': '#565A5B', // Default edge color
        opacity: 0.85, // More transparent edges with many nodes
        'z-index': 1,
        'transition-property': 'line-color, width, opacity',
        'transition-duration': 0.3,
        'curve-style': 'bezier', // Use bezier curves for smoother edges
        'line-style': 'dotted', // Dotted lines for edges
        'target-arrow-shape': 'none', // Use triangle arrows for edges
      },
    },
    {
      selector: 'edge.virtual',
      style: {
        display: 'none',
      },
    },
    {
      selector: 'node.highlighted',
      style: {
        'border-width': 4,
        'border-color': '#ef4444',
        'z-index': 20,
      },
    },
    {
      selector: 'edge.highlighted',
      style: {
        // label: 'data(label)',
        'line-color': '#0052cc',
        width: 1,
        'target-arrow-color': '#0052cc',
        'arrow-scale': 0.05, // 矢印のサイズを大きくする
        'curve-style': 'bezier',
        'line-style': 'solid', // 通常の線に戻す
        opacity: 0.5, // ハイライトされたエッジは不透明に
        'z-index': 15, // ハイライトされたエッジの z-index
        'text-margin-x': -100, // テキストの位置を調整
        'text-margin-y': -10, // テキストの位置を調整
        'text-halign': 'left', // テキストの水平位置を中央に
        'target-arrow-shape': 'none', // Use triangle arrows for edges
      },
    },
    {
      selector: 'edge.theme-highlighted',
      style: {
        // label: '', // ハイライトされたテーマエッジはラベルを非表示
      },
    },
    {
      selector: 'edge.unhighlighted',
      style: {
        width: 0.1,
        opacity: 0.15, // 非ハイライトエッジは透明度を下げる
      },
    },

    // Selected node styles
    {
      selector: 'node.selected',
      style: {
        'border-width': 5,
        'border-color': rwColors.green, // Green border
        'border-opacity': 1,
        'z-index': 30, // Higher than highlighted
        'font-weight': 'bold',
        'background-opacity': (ele: cytoscape.NodeSingular) => {
          const nodeType = ele.data('nodeType');
          return getNodeBackgroundOpacity(nodeType);
        },
        'background-color': (ele: cytoscape.NodeSingular) => {
          const nodeType = ele.data('nodeType');
          return getNodeColor(nodeType);
        },
        color: (ele: cytoscape.NodeSingular) => {
          const nodeType = ele.data('nodeType');
          return getNodeTextColor(nodeType);
        },
        display: 'element',
      },
    },
    {
      selector: 'node[nodeType="' + NodeType.THEME + '"].selected',
      style: {
        'border-width': 3,
        'border-color': rwColors.green, // Green border
        'border-opacity': 1,
        'z-index': 30, // Higher than highlighted
        'background-opacity': 1,
        'font-weight': 'bold',
        'text-background-opacity': 1,
        'text-background-color': rwColors.main,
        'text-background-padding': '3',
        color: rwColors.white,
      },
    },
    {
      selector: 'edge.selected',
      style: {
        'line-color': '#10b981',
        width: 3,
        opacity: 1,
        'z-index': 20,
        'target-arrow-color': '#10b981',
      },
    },
    {
      selector: 'node.selected-by-filter-highlight',
      style: {
        'border-width': 1,
        'border-color': '#ffc400', // 黄金のボーダー
        'z-index': 15, // ハイライトされたノードよりも下
        'font-weight': 'bold',
        'text-background-opacity': 0.8,
        'text-background-color': '#ffc400',
        'text-background-padding': '3',
        color: '#003388', // あおっぽい文字色
      },
    },
    // Persistent selection highlighting styles
    {
      selector: 'node.selected-highlight',
      style: {
        'border-color': '#10b981', // Green border for selected nodes
        'z-index': 25, // Higher than regular highlighted nodes
      },
    },
    {
      selector: 'edge.selected-highlight',
      style: {
        'line-color': '#a7f3d0', // Light green for selected edges
        opacity: 1,
        'z-index': 9,
      },
    },
    {
      selector: '.faded',
      style: {
        opacity: 0.15,
      },
    },
    {
      selector: 'node:locked',
      style: {
        'border-color': '#ff6b6b',
        'border-width': 3,
      },
    },
    {
      selector: 'node.searched',
      style: {
        'background-color': '#f7b439',
        'border-color': '#38eae1',
        'border-width': 4,
        'border-style': 'solid',
        // アニメーションはJavaScriptで制御するため、transition-propertyは削除
      },
    },
    {
      selector: 'edge.dimmed',
      style: {
        opacity: 0.15,
      },
    },
    {
      selector: 'node.filter-selected', // filterCondition で選択されたノードのスタイル
      style: {
        'border-width': 3,
        'border-color': '#ffff00', // 例: 黄色いボーダー
        // 他のスタイルプロパティ
      },
    },
  ]);
};
